import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import {
    ArrowUpRight,
    Facebook,
    Instagram,
    Mail,
    MapPin,
    Phone
} from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { WhatsAppIcon } from '@/components/generals/icons'

import {
    contactInfo,
    eventRegions,
    footerServiceLinks,
    navLinks,
    siteConfig,
    whatsappLink
} from '@/constants/site'

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='surface-night relative overflow-hidden text-white/70'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-px left-0 h-px w-full bg-linear-to-r from-transparent via-gold/50 to-transparent' />

            <div className='mx-auto max-w-7xl px-6 pt-20 pb-10'>
                <div className='grid gap-12 md:grid-cols-2 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <Logo />

                        <p className='mt-6 max-w-sm text-sm leading-relaxed'>
                            Guju Events is a premium event management company delivering unforgettable weddings, corporate, cultural and exhibition events across India. Trusted by leading brands.
                        </p>

                        <div className='mt-6 flex items-center gap-3'>
                            <a href={contactInfo.instagram} target='_blank' rel='noopener noreferrer' aria-label='Guju Events on Instagram' className='focus-ring inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-gold hover:text-gold'>
                                <Instagram className='size-4' aria-hidden='true' />
                            </a>

                            <a href={contactInfo.facebook} target='_blank' rel='noopener noreferrer' aria-label='Guju Events on Facebook' className='focus-ring inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-gold hover:text-gold'>
                                <Facebook className='size-4' aria-hidden='true' />
                            </a>

                            <a href={whatsappLink()} target='_blank' rel='noopener noreferrer' aria-label='Chat with Guju Events on WhatsApp' className='focus-ring inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-gold hover:text-gold'>
                                <WhatsAppIcon className='size-4' />
                            </a>
                        </div>
                    </div>

                    <div className='lg:col-span-2'>
                        <h2 className='text-xs font-bold tracking-[0.22em] text-gold uppercase'>
                            Explore
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm'>
                            {navLinks.map((link) => (
                                <li key={link.href}>
                                    <Link href={link.href} className='focus-ring rounded transition-colors hover:text-white'>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='lg:col-span-3'>
                        <h2 className='text-xs font-bold tracking-[0.22em] text-gold uppercase'>
                            Services
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm'>
                            {footerServiceLinks.map((link) => (
                                <li key={link.href}>
                                    <Link href={link.href} className='focus-ring rounded transition-colors hover:text-white'>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <h2 className='mt-8 text-xs font-bold tracking-[0.22em] text-gold uppercase'>
                            Our major events
                        </h2>

                        <p className='mt-4 text-sm leading-relaxed'>
                            {eventRegions.join(', ')}
                        </p>
                    </div>

                    <div className='lg:col-span-3'>
                        <h2 className='text-xs font-bold tracking-[0.22em] text-gold uppercase'>
                            Contact us
                        </h2>

                        <ul className='mt-5 space-y-4 text-sm'>
                            <li>
                                <a href={contactInfo.phoneHref} className='focus-ring inline-flex items-start gap-3 rounded transition-colors hover:text-white'>
                                    <Phone className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                    {contactInfo.phoneDisplay}
                                </a>
                            </li>

                            <li>
                                <a href={`mailto:${contactInfo.email}`} className='focus-ring inline-flex items-start gap-3 rounded break-all transition-colors hover:text-white'>
                                    <Mail className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                    {contactInfo.email}
                                </a>
                            </li>

                            <li>
                                <a href={contactInfo.mapsUrl} target='_blank' rel='noopener noreferrer' className='focus-ring inline-flex items-start gap-3 rounded transition-colors hover:text-white'>
                                    <MapPin className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                    {contactInfo.addressFull}
                                </a>
                            </li>
                        </ul>

                        <Link href='/contact#inquiry' className='focus-ring group mt-7 inline-flex items-center gap-2 rounded text-sm font-semibold text-white'>
                            Plan your event with us
                            <ArrowUpRight className='size-4 text-gold transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5' aria-hidden='true' />
                        </Link>
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-white/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        ©
                        {' '}
                        <span suppressHydrationWarning>{new Date().getFullYear()}</span>
                        {' '}
                        {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Weddings, corporate events, garba nights, concerts and manpower across India.
                    </p>
                </div>
            </div>
        </footer>
    )
}
